import { type ReactNode, Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setAfterLogin, takeAfterLogin, useSession } from './services/session';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import CreateRoomPage from './pages/CreateRoomPage';
import JoinPage from './pages/JoinPage';
import LobbyPage from './pages/LobbyPage';
import ResultPage from './pages/ResultPage';
import LeaderboardPage from './pages/LeaderboardPage';
import HistoryPage from './pages/HistoryPage';

// Phaser is only downloaded for the screens that render the board.
const PracticePage = lazy(() => import('./pages/PracticePage'));
const MatchPage = lazy(() => import('./pages/MatchPage'));

function Splash({ text = '불러오는 중…' }: { text?: string }) {
  return <div className="splash">{text}</div>;
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (status === 'anonymous') setAfterLogin(location.pathname + location.search);
  }, [status, location]);

  useEffect(() => {
    if (status === 'authenticated') {
      const next = takeAfterLogin();
      if (next && next !== location.pathname + location.search) navigate(next, { replace: true });
    }
  }, [status, navigate, location]);

  if (status === 'booting') return <Splash />;
  if (status === 'anonymous') return <LoginPage />;
  return <>{children}</>;
}

/** Crew entry point: /launch?next=/join/...#ticket=... (ticket consumed during boot). */
function Launch() {
  const location = useLocation();
  const next = new URLSearchParams(location.search).get('next');
  const safe = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';
  return <Navigate to={safe} replace />;
}

export default function App() {
  const boot = useSession((s) => s.boot);
  useEffect(() => {
    void boot();
  }, [boot]);

  const guard = (el: ReactNode) => <RequireAuth>{el}</RequireAuth>;
  return (
    <BrowserRouter>
      <Suspense fallback={<Splash text="게임 준비 중…" />}>
        <Routes>
          <Route path="/" element={guard(<HomePage />)} />
          <Route path="/launch" element={guard(<Launch />)} />
          <Route path="/practice" element={<PracticePage />} />
          <Route path="/rooms/new" element={guard(<CreateRoomPage />)} />
          <Route path="/rooms/:roomId" element={guard(<LobbyPage />)} />
          <Route path="/join/:roomId" element={guard(<JoinPage />)} />
          <Route path="/match/:matchId" element={guard(<MatchPage />)} />
          <Route path="/matches/:matchId/result" element={guard(<ResultPage />)} />
          <Route path="/leaderboard" element={guard(<LeaderboardPage />)} />
          <Route path="/history" element={guard(<HistoryPage />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
